import { isFunction, delay } from 'underscore';
import { createMachine, assign, sendParent } from 'xstate';
import { StepEvents } from './step-events';
import { WizardEvents } from './wizard-events';

const StepStates = {
  INIT: 'INIT',
  FAILED_INIT: 'FAILED_INIT',
  LOADING: 'LOADING',
  SAVING: 'SAVING',
  CANCELING: 'CANCELING',
  VALIDATING: 'VALIDATING',
  VALID: 'VALID',
  INVALID: 'INVALID',
  INACTIVE: 'INACTIVE',
  STOPPING: 'STOPPING',
  FIN: 'FIN',
};

// Public state selectors
export const isCanceling = state => state.matches(StepStates.CANCELING);
export const isStopping = state => state.matches(StepStates.STOPPING);
export const isFin = state => state.matches(StepStates.FIN);
export const isFailedInit = state => state.matches(StepStates.FAILED_INIT);
export const isActive = state => state.matches(StepStates.VALID) || state.matches(StepStates.INVALID);
export const isLoading = state => state.matches(StepStates.LOADING);
export const isSaving = state => state.matches(StepStates.SAVING);

export const contextSelector = state => state.context;

const defaultInitialContext = {
  stepsData: {},
  actionsOverride: {},
  guardsOverride: {},
  data: {
    data: {},
    isValid: true,
    isDirty: false,
  },
  invalidSaveAttempt: 0,
};

// Derived state accessors
const needsLoading = () => false;
const hasIncomingSystemError = () => false;
const isValid = context => context.data.isValid;
const isDirty = context => context.data.isDirty;
const needsSaving = isDirty;
const needsStopping = (context) => {
  if (isFunction(context.guardsOverride.needsStopping)) {
    return context.guardsOverride.needsStopping(context);
  }
  return false;
};
// State "assigners"
const replaceData = assign({
  data: (_, event) => event.data,
});
const patchStepsData = assign({
  stepsData: (context, event) => ({
    ...context.stepsData,
    ...event.stepsData,
  }),
});
const replaceStepsData = assign({
  stepsData: (_, event) => event.stepsData,
});
const patchData = assign({
  data: (context, event) => ({
    ...context.data,
    ...event.data,
  }),
});
const clearErrors = () => { };
const onInvalidSaveAttempt = assign({
  // eslint-disable-next-line no-bitwise
  invalidSaveAttempt: context => (context.invalidSaveAttempt | 0) + 1,
});
const overrideActions = assign({
  actionsOverride: (context, event) => ({
    ...context.actionsOverride,
    ...event.data,
  }),
});
const overrideGuards = assign({
  guardsOverride: (context, event) => ({
    ...context.guardsOverride,
    ...event.data,
  }),
});

const notifyLoading = sendParent(WizardEvents.STEP_LOADING);
const notifyDoneLoading = sendParent(WizardEvents.STEP_DONE_LOADING);
// non-recoverable step failure
const notifyFailedLoading = sendParent(WizardEvents.STEP_FAILED_LOADING);

const notifySaving = sendParent(WizardEvents.STEP_SAVING);
const notifyDoneSaving = sendParent((context/* , event */) => ({
  type: WizardEvents.STEP_DONE_SAVING,
  stepsData: context.stepsData,
}));
const notifyDoneCanceling = sendParent({ type: WizardEvents.STEP_DONE_CANCELING });
const notifyAbortedSaving = sendParent(WizardEvents.STEP_ABORTED_SAVING);
const notifyAbortedCanceling = sendParent(WizardEvents.STEP_ABORTED_CANCELING);
const notifyStopped = sendParent({ type: WizardEvents.STEP_STOPPED });
const requestConfirmation = context => new Promise((resolve, reject) => {
  delay(() => {
    const response = { aborted: false };
    if (isFunction(context.actionsOverride.confirmCancel)) {
      if (!context.actionsOverride.confirmCancel()) {
        response.aborted = true;
      }
    }
    if (response.aborted) {
      reject();
    } else {
      resolve();
    }
  }, 200);
});


const defaultActions = {
  notifyLoading,
  notifySaving,
  notifyAbortedSaving,
  notifyAbortedCanceling,
  notifyFailedLoading,
  notifyDoneLoading,
  notifyDoneSaving,
  notifyDoneCanceling,
  notifyStopped,

  replaceData,
  patchData,
  replaceStepsData,
  patchStepsData,

  clearErrors,
  onInvalidSaveAttempt,

  overrideActions,
  overrideGuards,
};

const defaultServices = {
  requestConfirmation,
};

const defaultGuards = {
  needsLoading,
  isValid,
  needsSaving,
  needsStopping,
};


export const stepStateMachine = ({
  machineId = 'StepStateMachine',
  initialContext = defaultInitialContext,
  actions = defaultActions,
  services = defaultServices,
  guards = defaultGuards,
} = {}) => createMachine({
  preserveActionOrder: true,
  predictableActionArguments: true,
  strict: true,

  id: machineId,
  context: {
    ...defaultInitialContext,
    ...initialContext,
  },
  initial: StepStates.INIT,
  on: {
    [StepEvents.OVERRIDE_ACTIONS]: [
      { actions: 'overrideActions' },
    ],
    [StepEvents.OVERRIDE_GUARDS]: [
      { actions: 'overrideGuards' },
    ],
    [StepEvents.STOP]: [
      { target: StepStates.STOPPING, cond: 'needsStopping' },
      StepStates.FIN,
    ],
  },
  states: {
    [StepStates.INIT]: {
      always: [
        { target: StepStates.LOADING, cond: 'needsLoading' },
        { target: StepStates.VALIDATING },
      ],
    },
    [StepStates.LOADING]: {
      entry: ['notifyLoading'],
      on: {
        [StepEvents.DONE_LOADING]: {
          target: StepStates.VALIDATING,
          actions: [
            'replaceData',
            'patchStepsData',
            'notifyDoneLoading',
          ],
        },
        [StepEvents.FAILED_LOADING]: [
          {
            target: StepStates.FAILED_INIT,
            cond: hasIncomingSystemError,
            actions: [
              'replaceData',
              'patchStepsData',
              'notifyFailedLoading',
            ],
          },
          {
            target: StepStates.VALIDATING,
            actions: [
              'replaceData',
              'patchStepsData',
              'notifyDoneLoading',
            ],
          },
        ],
      },
    },
    [StepStates.FAILED_INIT]: {},
    [StepStates.SAVING]: {
      entry: ['clearErrors', 'notifySaving'],
      on: {
        [StepEvents.DONE_SAVING]: {
          target: StepStates.VALIDATING,
          actions: [
            'replaceData',
            'patchStepsData',
            'notifyDoneSaving',
          ],
        },
        [StepEvents.FAILED_SAVING]: {
          target: StepStates.VALIDATING,
          actions: [
            'replaceData',
            'patchStepsData',
            'onInvalidSaveAttempt',
            'notifyAbortedSaving',
          ],
        },
      },
    },
    [StepStates.CANCELING]: {
      invoke: {
        id: 'requestConfirmationCallback',
        src: 'requestConfirmation',
        onDone: {
          target: StepStates.VALIDATING,
          actions: ['notifyDoneCanceling'],
        },
        onError: {
          target: StepStates.VALIDATING,
          actions: ['notifyAbortedCanceling'],
        },
      },
    },
    [StepStates.STOPPING]: {
      on: {
        [StepEvents.DONE_STOPPING]: [StepStates.FIN],
      },
    },
    [StepStates.VALIDATING]: {
      always: [
        { target: StepStates.VALID, cond: 'isValid' },
        { target: StepStates.INVALID },
      ],
    },
    [StepStates.VALID]: {
      on: {
        [StepEvents.UPDATE_VALIDATION]: [
          {
            target: StepStates.VALIDATING,
            actions: ['patchData'],
          },
        ],
        [StepEvents.CANCEL]: [
          { target: StepStates.CANCELING, cond: 'needsSaving' },
          { actions: ['notifyDoneCanceling'] },
        ],
        [StepEvents.SAVE]: [
          { target: StepStates.SAVING, cond: 'needsSaving' },
          { actions: ['notifyDoneSaving'] },
        ],
        [StepEvents.DEACTIVATE]: StepStates.INACTIVE,
      },
    },
    [StepStates.INVALID]: {
      on: {
        [StepEvents.UPDATE_VALIDATION]: [
          {
            target: StepStates.VALIDATING,
            actions: ['patchData'],
          },
        ],
        [StepEvents.CANCEL]: [
          { target: StepStates.CANCELING, cond: 'needsSaving' },
          { actions: ['notifyDoneCanceling'] },
        ],
        [StepEvents.SAVE]: {
          actions: ['onInvalidSaveAttempt', 'notifyAbortedSaving'],
        },
        [StepEvents.DEACTIVATE]: StepStates.INACTIVE,
      },
    },
    [StepStates.INACTIVE]: {
      on: {
        [StepEvents.ACTIVATE]: [
          { target: StepStates.LOADING, cond: 'needsLoading', actions: ['replaceStepsData'] },
          { target: StepStates.VALIDATING, actions: ['replaceStepsData'] },
        ],
      },
    },
    [StepStates.FIN]: {
      entry: ['notifyStopped'],
    },
  },
}, {
  actions: {
    ...defaultActions,
    ...actions,
  },
  services: {
    ...defaultServices,
    ...services,
  },
  guards: {
    ...defaultGuards,
    ...guards,
  },
});
